import React, { useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {
  PhotoIcon,
  VideoCameraIcon,
  CodeBracketIcon,
  EyeIcon,
  PencilIcon,
  SwatchIcon,
} from "@heroicons/react/24/outline";

interface DynamicMarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
}

const DynamicMarkdownEditor: React.FC<DynamicMarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = "Write your content here...",
  error,
}) => {
  const [selectedTab, setSelectedTab] = useState<"write" | "preview">("write");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Quick insert functions
  const insertAtCursor = (
    before: string,
    after: string = "",
    placeholder: string = ""
  ) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newValue =
      value.substring(0, start) +
      before +
      textToInsert +
      after +
      value.substring(end);

    onChange(newValue);

    // Set cursor position after insertion
    setTimeout(() => {
      const newCursorPos =
        start + before.length + textToInsert.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const insertImage = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      const alt = prompt("Enter alt text (optional):") || "Image";
      insertAtCursor(`![${alt}](${url})\n\n`);
    }
  };

  const insertVideo = () => {
    const url = prompt("Enter video URL (YouTube, Vimeo, etc.):");
    if (url) {
      if (url.includes("youtube.com") || url.includes("youtu.be")) {
        // Extract YouTube video ID
        let videoId = "";
        if (url.includes("youtu.be/")) {
          videoId = url.split("youtu.be/")[1].split("?")[0];
        } else if (url.includes("watch?v=")) {
          videoId = url.split("watch?v=")[1].split("&")[0];
        }

        if (videoId) {
          insertAtCursor(`
<div className="video-container my-6">
  <iframe 
    width="560" 
    height="315" 
    src="https://www.youtube.com/embed/${videoId}" 
    title="YouTube video player" 
    frameBorder="0" 
    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
    allowFullScreen
    className="w-full rounded-lg"
  ></iframe>
</div>

`);
        }
      } else {
        // Regular video
        insertAtCursor(`
<video controls className="w-full rounded-lg my-6">
  <source src="${url}" type="video/mp4" />
  Your browser does not support the video tag.
</video>

`);
      }
    }
  };

  const insertHighlight = () => {
    insertAtCursor("<mark>", "</mark>", "highlighted text");
  };

  const insertColoredText = () => {
    const color = prompt("Enter a color (name or hex, e.g. crimson or #e13238):");
    if (color) {
      insertAtCursor(`<span style="color: ${color}">`, "</span>", "colored text");
    }
  };

  const insertCodeBlock = () => {
    const language = prompt("Enter programming language (optional):") || "";
    insertAtCursor(`\`\`\`${language}\n`, "\n```\n\n", "Your code here");
  };

  const insertTable = () => {
    const tableMarkdown = `
| Header 1 | Header 2 | Header 3 |
|----------|----------|----------|
| Cell 1   | Cell 2   | Cell 3   |
| Cell 4   | Cell 5   | Cell 6   |

`;
    insertAtCursor(tableMarkdown);
  };

  const insertAlert = (type: "info" | "warning" | "success" | "error") => {
    const icons = {
      info: "💡",
      warning: "⚠️",
      success: "✅",
      error: "❌",
    };
    const colors = {
      info: "bg-blue-50 border-blue-200 text-blue-800",
      warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
      success: "bg-green-50 border-green-200 text-green-800",
      error: "bg-red-50 border-red-200 text-red-800",
    };

    insertAtCursor(
      `
<div className="${colors[type]} border-l-4 p-4 my-4">
  <div className="flex">
    <span className="mr-2">${icons[type]}</span>
    <div>
      <p className="font-medium">`,
      `</p>
      <p>Add your message here</p>
    </div>
  </div>
</div>

`
    );
  };

  const toolbar = (
    <div className="border-b border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-800 rounded-t-lg">
      {/* Mode Tabs */}
      <div className="flex space-x-1 mb-3">
        <button
          type="button"
          onClick={() => setSelectedTab("write")}
          className={`px-3 py-1 rounded text-sm font-medium ${
            selectedTab === "write"
              ? "bg-blue-500 text-white"
              : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          }`}
        >
          <PencilIcon className="w-4 h-4 inline mr-1" />
          Write
        </button>
        <button
          type="button"
          onClick={() => setSelectedTab("preview")}
          className={`px-3 py-1 rounded text-sm font-medium ${
            selectedTab === "preview"
              ? "bg-blue-500 text-white"
              : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          }`}
        >
          <EyeIcon className="w-4 h-4 inline mr-1" />
          Preview
        </button>
      </div>

      {/* Toolbar Actions */}
      {selectedTab === "write" && (
        <div className="flex flex-wrap gap-2">
          {/* Text Formatting */}
          <div className="flex space-x-1 border-r border-gray-300 dark:border-gray-600 pr-2">
            <button
              type="button"
              onClick={() => insertAtCursor("**", "**", "bold text")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Bold"
            >
              <span className="font-bold">B</span>
            </button>
            <button
              type="button"
              onClick={() => insertAtCursor("*", "*", "italic text")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Italic"
            >
              <span className="italic">I</span>
            </button>
            <button
              type="button"
              onClick={() => insertAtCursor("`", "`", "code")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 font-mono"
              title="Inline Code"
            >
              {"</>"}
            </button>
            <button
              type="button"
              onClick={insertHighlight}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Highlight"
            >
              <span style={{ background: "#fde68a", padding: "0 3px", borderRadius: 2 }}>H</span>
            </button>
            <button
              type="button"
              onClick={insertColoredText}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Text color"
            >
              <SwatchIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Headers */}
          <div className="flex space-x-1 border-r border-gray-300 dark:border-gray-600 pr-2">
            <button
              type="button"
              onClick={() => insertAtCursor("# ", "", "Heading 1")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Heading 1"
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => insertAtCursor("## ", "", "Heading 2")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Heading 2"
            >
              H2
            </button>
            <button
              type="button"
              onClick={() => insertAtCursor("### ", "", "Heading 3")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Heading 3"
            >
              H3
            </button>
          </div>

          {/* Media */}
          <div className="flex space-x-1 border-r border-gray-300 dark:border-gray-600 pr-2">
            <button
              type="button"
              onClick={insertImage}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Insert Image"
            >
              <PhotoIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={insertVideo}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Insert Video"
            >
              <VideoCameraIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={insertCodeBlock}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Code Block"
            >
              <CodeBracketIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Lists and Tables */}
          <div className="flex space-x-1 border-r border-gray-300 dark:border-gray-600 pr-2">
            <button
              type="button"
              onClick={() => insertAtCursor("- ", "", "List item")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Bullet List"
            >
              ●
            </button>
            <button
              type="button"
              onClick={() => insertAtCursor("1. ", "", "List item")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Numbered List"
            >
              1.
            </button>
            <button
              type="button"
              onClick={insertTable}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              title="Insert Table"
            >
              ⊞
            </button>
          </div>

          {/* Alerts */}
          <div className="flex space-x-1">
            <button
              type="button"
              onClick={() => insertAlert("info")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-blue-600"
              title="Info Alert"
            >
              💡
            </button>
            <button
              type="button"
              onClick={() => insertAlert("warning")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-yellow-600"
              title="Warning Alert"
            >
              ⚠️
            </button>
            <button
              type="button"
              onClick={() => insertAlert("success")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-green-600"
              title="Success Alert"
            >
              ✅
            </button>
            <button
              type="button"
              onClick={() => insertAlert("error")}
              className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-red-600"
              title="Error Alert"
            >
              ❌
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full">
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
        {toolbar}

        {selectedTab === "write" ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={16}
            className="w-full p-4 border-0 focus:ring-0 focus:outline-none resize-none dark:bg-gray-700 dark:text-white font-mono text-sm leading-6"
            style={{ minHeight: "400px" }}
          />
        ) : (
          <div className="p-4 prose prose-gray dark:prose-invert max-w-none min-h-[400px] overflow-auto">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                // Custom components for better rendering
                img: ({ node, alt, ...props }) => (
                  <img
                    {...props}
                    alt={alt || ""}
                    className="rounded-lg max-w-full h-auto my-4"
                  />
                ),
                video: ({ node, ...props }) => (
                  <video
                    {...props}
                    className="rounded-lg max-w-full h-auto my-4"
                    controls
                  />
                ),
                code: ({ node, className, children, ...props }: any) => {
                  const match = /language-(\w+)/.exec(className || "");
                  return match ? (
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4">
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  ) : (
                    <code
                      className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-4">
                    <table
                      className="min-w-full border-collapse border border-gray-300 dark:border-gray-600"
                      {...props}
                    />
                  </div>
                ),
                th: ({ node, ...props }) => (
                  <th
                    className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 px-4 py-2 text-left"
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <td
                    className="border border-gray-300 dark:border-gray-600 px-4 py-2"
                    {...props}
                  />
                ),
              }}
            >
              {value || "*Nothing to preview yet...*"}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {error && <div className="mt-1 text-sm text-red-600">{error}</div>}

      <div className="mt-2 text-xs text-gray-500">
        <p>
          <strong>Pro tip:</strong> You can embed YouTube videos, images, GIFs,
          code blocks, and more! Use the toolbar buttons or write Markdown
          directly.
        </p>
      </div>
    </div>
  );
};

export default DynamicMarkdownEditor;
